/**
 * Which job body string to show on the job detail page: always prefer the faithful
 * employer extract (`originalDescription`) over the derived rewrite (`description`).
 */

const LOCALE_TRY_ORDER = (locale: string): string[] => {
  const l = locale.toLowerCase();
  const rest = ['en', 'fi', 'sv'].filter((x) => x !== l);
  return [l, ...rest];
};

export type JobDescriptionBodySource = 'original' | 'derived' | 'none';

export type ResolvedJobDescriptionBody = {
  body: string;
  source: JobDescriptionBodySource;
  /**
   * When resolving from suffixed API fields: which locale key supplied `body`
   * (e.g. "en" for originalDescription_en). "unsuffixed" = legacy single field on the record.
   */
  sourceFieldLocale?: string;
};

type DisplayJobLike = {
  originalDescription?: string | null;
  description?: string | null;
};

/**
 * After `transformJobForLanguage`, fields are merged onto base keys.
 */
export function resolveJobDisplayDescriptionFromDisplayJob(job: DisplayJobLike): ResolvedJobDescriptionBody {
  const o = typeof job.originalDescription === 'string' ? job.originalDescription.trim() : '';
  if (o) return { body: o, source: 'original' };
  const d = typeof job.description === 'string' ? job.description.trim() : '';
  if (d) return { body: d, source: 'derived' };
  return { body: '', source: 'none' };
}

/**
 * Raw API / Algolia record with per-locale suffix keys (before transform).
 */
export function resolveJobDisplayDescriptionFromLocaleRecord(
  job: Record<string, unknown>,
  locale: string,
): ResolvedJobDescriptionBody {
  const order = LOCALE_TRY_ORDER(locale);

  for (const l of order) {
    const v = job[`originalDescription_${l}`];
    if (typeof v === 'string' && v.trim()) {
      return { body: v.trim(), source: 'original', sourceFieldLocale: l };
    }
  }

  const legacyOriginal = job.originalDescription;
  if (typeof legacyOriginal === 'string' && legacyOriginal.trim()) {
    return { body: legacyOriginal.trim(), source: 'original', sourceFieldLocale: 'unsuffixed' };
  }

  for (const l of order) {
    const v = job[`description_${l}`];
    if (typeof v === 'string' && v.trim()) {
      return { body: v.trim(), source: 'derived', sourceFieldLocale: l };
    }
  }

  const legacyDerived = job.description;
  if (typeof legacyDerived === 'string' && legacyDerived.trim()) {
    return { body: legacyDerived.trim(), source: 'derived', sourceFieldLocale: 'unsuffixed' };
  }

  return { body: '', source: 'none' };
}

export function pickJobDisplayDescriptionFromDisplayJob(job: DisplayJobLike): string {
  return resolveJobDisplayDescriptionFromDisplayJob(job).body;
}

export function pickJobDisplayDescriptionFromLocaleRecord(job: Record<string, unknown>, locale: string): string {
  return resolveJobDisplayDescriptionFromLocaleRecord(job, locale).body;
}

/**
 * Job detail UI: show which description field is driving the body (dev, or `?describeSource=1` in production).
 */
export function shouldShowJobDescriptionSourceDebug(urlString: string | undefined): boolean {
  if (import.meta.env.DEV) return true;
  if (!urlString) return false;
  try {
    return new URL(urlString, 'http://localhost').searchParams.get('describeSource') === '1';
  } catch {
    return false;
  }
}
